<template>
    <PopupTemplate
        v-model="isOpen"
        :img-raw="popupImageRaw"
        :img-alt="popupImageAlt"
        :title="popupTitle"
        :body-class="popupBodyClass"
        :body-max-width="popupBodyMaxWidth"
        @close="handleClose"
    >
        <template #body>
            <DomainWizardBillingUpdate
                v-if="isDomainUnparkMode"
                v-model="agreementAccepted"
                :title="billingUpdateTitle"
                :message="billingUpdateCopy"
                :items="billingLimitReached ? [] : billingUpdateItems"
                :agreement-text="
                    billingLimitReached
                        ? undefined
                        : t('vue.domain_wizard_billing_agreement')
                "
                :variant="billingLimitReached ? 'danger' : 'warning'"
                :show-items="!billingLimitReached"
                :show-agreement="!billingLimitReached"
                surface-variant="white"
                align="left"
                :full-width="true"
            />

            <div v-else class="free-plan-post-popup-description">
                <p
                    v-html="
                        t(
                            'vue.free_plan_post_continue_popup_description_primary_html',
                        )
                    "
                />
                <p>
                    {{
                        t(
                            "vue.free_plan_post_continue_popup_description_secondary",
                        )
                    }}
                </p>
            </div>
        </template>

        <template #actions>
            <AtomicButton
                v-if="isDomainUnparkMode"
                color="yellow"
                size="lg"
                class="border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-800 focus:ring-gray-200"
                @click="handleClose"
            >
                {{ t("vue.nope") }}
            </AtomicButton>

            <AtomicButton
                color="yellow"
                size="lg"
                class="border-transparent bg-[#088BEA] text-white hover:text-gray-100 focus:ring-[#088BEA] disabled:bg-gray-300 disabled:text-white"
                :disabled="confirmDisabled"
                @click="handlePrimaryAction"
            >
                {{ primaryActionLabel }}
            </AtomicButton>
        </template>
    </PopupTemplate>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { usePage } from "@inertiajs/vue3";
import axios from "axios";
import route from "ziggy-js";
import imgSrc from "@/assets/images/free-plan-post-continue.svg?raw";
import PopupTemplate from "@/components/Atomic/Dialogs/PopupTemplate.vue";
import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import { useTranslations } from "@/composables/useTranslations.ts";
import DomainWizardBillingUpdate from "@/components/Special/DomainWizardBillingUpdate.vue";
import type { IPaddleSubscription } from "@/types/Subscriptions/IPaddleSubscription";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";

const props = defineProps<{
    modelValue: boolean;
    mode?: "default" | "domain-unpark";
    unparkedDomainsCount?: number;
}>();

const emit = defineEmits<{
    (e: "update:modelValue", value: boolean): void;
    (e: "close"): void;
    (e: "confirm"): void;
}>();

const { t } = useTranslations();
const page = usePage<IRhizomPageProps>();

const agreementAccepted = ref(false);
const subscriptionData = ref<IPaddleSubscription | null>(null);
const subscriptionLoading = ref(false);

const isDomainUnparkMode = computed(() => props.mode === "domain-unpark");
const unparkedDomainsCount = computed(() => props.unparkedDomainsCount ?? 0);

const hasGodMode = computed(() => page.props.hasGodMode === true);
const isTrialing = computed(() => page.props.isTrialing === true);
const hasBasicPlan = computed(() => page.props.hasBasicPlan === true);
const hasScalePlan = computed(() => page.props.hasScalePlan === true);
const hasFreePlan = computed(() => page.props.hasFreePlan === true);
const paidYearly = computed(() => page.props.paidYearly === true);
const subscriptionLevel = computed(() =>
    String(page.props.team?.subscription_level ?? "trial").toLowerCase(),
);

const isTrialLevel = computed(() =>
    ["trialing", "trial-expired", "trial"].includes(subscriptionLevel.value),
);
const isCancelledLevel = computed(
    () => subscriptionLevel.value === "subscription-cancelled",
);
const isEnterpriseLevel = computed(
    () => subscriptionLevel.value === "enterprise",
);
const isFreePlanLevel = computed(() => subscriptionLevel.value === "free-plan");

const canAddDomain = computed(() => {
    if (hasGodMode.value) {
        return true;
    }

    if (isFreePlanLevel.value || hasFreePlan.value) {
        return unparkedDomainsCount.value < 1;
    }

    if (hasBasicPlan.value && !hasScalePlan.value) {
        return unparkedDomainsCount.value < 1;
    }

    if (hasScalePlan.value) {
        return unparkedDomainsCount.value < 50;
    }

    return isTrialing.value;
});

const showBillingUpdate = computed(() => {
    if (isDomainUnparkMode.value !== true) {
        return false;
    }

    if (!hasBasicPlan.value && !hasScalePlan.value) {
        return false;
    }

    if (isTrialing.value) {
        return false;
    }

    if (
        isTrialLevel.value ||
        isCancelledLevel.value ||
        isEnterpriseLevel.value
    ) {
        return false;
    }

    if (hasGodMode.value) {
        return false;
    }

    return true;
});

const billingLimitReached = computed(
    () =>
        showBillingUpdate.value &&
        !canAddDomain.value &&
        (hasBasicPlan.value || hasScalePlan.value),
);

const billingUpdateTitle = computed(() =>
    billingLimitReached.value
        ? t("vue.youve_reached_your_limit")
        : t("vue.billing_update"),
);

const billingUpdateMessage = computed(() => {
    if (paidYearly.value) {
        return t("vue.billing_update_yearly_message");
    }

    return t("vue.billing_update_monthly_message");
});

const billingUpdateCopy = computed(() => {
    if (!billingLimitReached.value) {
        return billingUpdateMessage.value;
    }

    if (hasBasicPlan.value && !hasScalePlan.value) {
        return t("vue.cannot_add_more_domains_basic_plan");
    }

    if (hasScalePlan.value) {
        return t("vue.cannot_add_more_domains_scale_plan");
    }

    return t("vue.reached_your_limit_body");
});

const billingPlanLabel = computed(() => {
    const name = subscriptionData.value?.items?.[0]?.productName;
    if (name) {
        return name;
    }

    const fallback = subscriptionLevel.value;
    if (!fallback) {
        return "-";
    }

    return fallback
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
});

const billingPeriodLabel = computed(() => {
    const period =
        subscriptionData.value?.period ??
        (paidYearly.value ? "yearly" : "monthly");
    if (period === "year" || period === "yearly") {
        return t("vue.billing_period_yearly");
    }

    if (period === "month" || period === "monthly") {
        return t("vue.billing_period_monthly");
    }

    return "-";
});

const billingPeriodEnd = computed(() => {
    const date = subscriptionData.value?.currentBillingPeriod?.end?.date;
    if (!date) {
        return "-";
    }

    return new Date(date).toLocaleDateString();
});

const billingDomainPrice = computed(() => {
    const price = subscriptionData.value?.items?.[0]?.unitPrice;
    return formatCurrency(price);
});

const billingProratedCostOld = computed(() => {
    const total = subscriptionData.value?.totals?.total;
    return formatCurrency(total);
});

const billingProratedCostNew = computed(() => {
    const currentTotal = subscriptionData.value?.totals?.total;
    const domainUnitPrice = subscriptionData.value?.items?.[0]?.unitPrice;

    if (currentTotal == null || domainUnitPrice == null) {
        return "-";
    }

    return formatCurrency(currentTotal + domainUnitPrice);
});

const billingUpdateItems = computed(() => [
    {
        label: t("vue.billing_update_current_plan"),
        value: billingPlanLabel.value,
    },
    {
        label: t("vue.billing_update_billing_period"),
        value: billingPeriodLabel.value,
    },
    {
        label: t("vue.billing_update_period_end"),
        value: billingPeriodEnd.value,
    },
    {
        label: t("vue.billing_update_domain_month"),
        value: billingDomainPrice.value,
    },
    {
        label: t("vue.billing_update_prorated_cost_old"),
        value: billingProratedCostOld.value,
    },
    {
        label: t("vue.billing_update_prorated_cost"),
        value: billingProratedCostNew.value,
    },
]);

const isOpen = computed({
    get: () => props.modelValue,
    set: (value: boolean) => emit("update:modelValue", value),
});

const popupImageRaw = computed(() =>
    isDomainUnparkMode.value ? undefined : imgSrc,
);
const popupImageAlt = computed(() =>
    isDomainUnparkMode.value
        ? ""
        : t("vue.free_plan_post_continue_popup_img_alt"),
);
const popupTitle = computed(() =>
    isDomainUnparkMode.value
        ? t("vue.unpark_domain")
        : t("vue.free_plan_post_continue_popup_title"),
);
const popupBodyClass = computed(() =>
    isDomainUnparkMode.value ? "sm:px-0" : "",
);
const popupBodyMaxWidth = computed<number | undefined>(() =>
    isDomainUnparkMode.value ? 520 : undefined,
);
const primaryActionLabel = computed(() =>
    isDomainUnparkMode.value
        ? t("vue.confirm")
        : t("vue.free_plan_post_popup_continue_button"),
);
const confirmDisabled = computed(() => {
    if (!isDomainUnparkMode.value) {
        return false;
    }

    if (!showBillingUpdate.value) {
        return false;
    }

    if (billingLimitReached.value) {
        return true;
    }

    return agreementAccepted.value !== true;
});

watch(
    () => props.modelValue,
    async (isVisible) => {
        if (isVisible !== true || isDomainUnparkMode.value !== true) {
            return;
        }

        agreementAccepted.value = false;

        if (
            showBillingUpdate.value !== true ||
            billingLimitReached.value === true
        ) {
            return;
        }

        await loadSubscriptionData();
    },
);

function handleClose(): void {
    emit("update:modelValue", false);
    emit("close");
}

function handlePrimaryAction(): void {
    if (isDomainUnparkMode.value !== true) {
        handleClose();
        return;
    }

    if (confirmDisabled.value) {
        return;
    }

    emit("confirm");
    emit("update:modelValue", false);
    emit("close");
}

function formatCurrency(value?: number | null): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return "-";
    }

    return `$${Number(value).toFixed(2)}`;
}

async function loadSubscriptionData(): Promise<void> {
    if (subscriptionLoading.value) {
        return;
    }

    subscriptionLoading.value = true;

    try {
        const response = await axios.get(
            route("billing.subscriptions.dataInfo"),
        );
        subscriptionData.value = response.data.paddleSubscription ?? null;
    } catch (error) {
        console.error("Failed to load subscription data:", error);
    } finally {
        subscriptionLoading.value = false;
    }
}
</script>

<style scoped>
.free-plan-post-popup-description {
    display: flex;
    flex-direction: column;
    gap: 18px;
    color: #3f3f46;
    text-align: center;
    font-family: Inter, sans-serif;
    font-size: 12.25px;
    font-style: normal;
    font-weight: 400;
    line-height: 142%;
}

.free-plan-post-popup-description p {
    margin: 0;
}
</style>
